import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Flex, chakra } from '@chakra-ui/react';
import {
  UserIcon as UserOutline,
  HomeIcon as HomeOutline,
  ChatBubbleOvalLeftEllipsisIcon as ChatOutline,
  ShoppingBagIcon as BagOutline,
  CubeIcon as CubeOutline,
  ChevronLeftIcon,
  ChevronRightIcon,
  Cog6ToothIcon as SettingsOutline,
  ArrowRightStartOnRectangleIcon as LogoutOutline,
} from '@heroicons/react/24/outline';
import {
  UserIcon as UserSolid,
  HomeIcon as HomeSolid,
  ChatBubbleOvalLeftEllipsisIcon as ChatSolid,
  ShoppingBagIcon as BagSolid,
  CubeIcon as CubeSolid,
  Cog6ToothIcon as SettingsSolid,
} from '@heroicons/react/24/solid';
import { TabIcon } from '../TabIcon/TabIcon';
import { useUser } from '@/constants/UserContext';
import { useAuth } from '@/contexts/AuthContext';
import { useChatUnread } from '@/hooks/useChatUnread';
import { formatUnreadCount } from '@/utils/unreadCount';

const SIDEBAR_EXPANDED_KEY = 'volyx-sidebar-expanded';
const SIDEBAR_WIDTH_COLLAPSED = 80;
const SIDEBAR_WIDTH_EXPANDED = 240;
const DESKTOP_BREAKPOINT_PX = 1024;
type HeroIcon = React.ComponentType<React.SVGProps<SVGSVGElement>>;

interface Tab {
  key: string;
  title: string;
  path: string;
  OutlineIcon: HeroIcon;
  SolidIcon: HeroIcon;
}

const VOLUNTEER_TABS: Tab[] = [
  {
    key: 'organizations',
    title: 'Home',
    path: '/organizations',
    OutlineIcon: HomeOutline,
    SolidIcon: HomeSolid,
  },
  {
    key: 'communication',
    title: 'Communication',
    path: '/communication',
    OutlineIcon: ChatOutline,
    SolidIcon: ChatSolid,
  },
  {
    key: 'store',
    title: 'Store',
    path: '/store',
    OutlineIcon: BagOutline,
    SolidIcon: BagSolid,
  },
  {
    key: 'profile',
    title: 'Your Profile',
    path: '/profile',
    OutlineIcon: UserOutline,
    SolidIcon: UserSolid,
  },
];

const ORGANIZATION_TABS: Tab[] = [
  {
    key: 'organizations',
    title: 'Home',
    path: '/organizations',
    OutlineIcon: HomeOutline,
    SolidIcon: HomeSolid,
  },
  {
    key: 'communication',
    title: 'Communication',
    path: '/communication',
    OutlineIcon: ChatOutline,
    SolidIcon: ChatSolid,
  },
  {
    key: 'volunteers',
    title: 'Dashboard',
    path: '/volunteers',
    OutlineIcon: CubeOutline,
    SolidIcon: CubeSolid,
  },
  {
    key: 'store',
    title: 'Store',
    path: '/store',
    OutlineIcon: BagOutline,
    SolidIcon: BagSolid,
  },
  {
    key: 'profile',
    title: 'Your Profile',
    path: '/profile',
    OutlineIcon: UserOutline,
    SolidIcon: UserSolid,
  },
];

const SETTINGS_TAB: Tab = {
  key: 'settings',
  title: 'Settings',
  path: '/settings',
  OutlineIcon: SettingsOutline,
  SolidIcon: SettingsSolid,
};

export function NavBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();
  const { user } = useUser();
  const { total: chatUnread } = useChatUnread();
  const userRole = user?.role ?? 'volunteer';
  const [isDesktop, setIsDesktop] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_EXPANDED_KEY) !== 'false';
    } catch {
      return true;
    }
  });
  const [isToggling, setIsToggling] = useState(false);

  useEffect(() => {
    const updateLayout = () => {
      const desktop = window.innerWidth >= DESKTOP_BREAKPOINT_PX;
      setIsDesktop(desktop);
      if (desktop) {
        const width = sidebarExpanded
          ? SIDEBAR_WIDTH_EXPANDED
          : SIDEBAR_WIDTH_COLLAPSED;
        document.documentElement.style.setProperty('--sidebar-width', `${width}px`);
      } else {
        document.documentElement.style.setProperty('--sidebar-width', '0px');
      }
    };
    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => {
      window.removeEventListener('resize', updateLayout);
      document.documentElement.style.setProperty('--sidebar-width', '240px');
    };
  }, [sidebarExpanded]);

  const toggleSidebar = () => {
    setIsToggling(true);
    setSidebarExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_EXPANDED_KEY, String(next));
      } catch {
        // Storage can be unavailable (private mode); the toggle still works for this session.
      }
      setTimeout(() => setIsToggling(false), 300);
      return next;
    });
  };

  const tabs = useMemo(() => {
    const baseTabs = userRole === 'organization' ? ORGANIZATION_TABS : VOLUNTEER_TABS;
    return isDesktop ? [...baseTabs, SETTINGS_TAB] : baseTabs;
  }, [userRole, isDesktop]);

  const handleTabClick = (path: string) => {
    navigate(path);
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const sidebarCollapsed = isDesktop && !sidebarExpanded;
  const sidebarWidth = sidebarCollapsed
    ? SIDEBAR_WIDTH_COLLAPSED
    : SIDEBAR_WIDTH_EXPANDED;

  return (
    <Box
      as="nav"
      position="fixed"
      bg="white"
      zIndex={100}
      {...(isDesktop
        ? {
            top: '0',
            left: '0',
            bottom: '0',
            w: `${sidebarWidth}px`,
            display: 'flex',
            flexDir: 'column' as const,
            borderRight: '1px solid',
            borderColor: '#e1e5eb',
            p: '4',
            px: sidebarCollapsed ? '2' : '3',
            // Fixed element must consume its own safe-area insets
            pt: 'max(1rem, env(safe-area-inset-top, 1rem))',
            pb: 'max(1rem, env(safe-area-inset-bottom, 1rem))',
            boxShadow: '2px 0 4px rgba(0,0,0,0.1)',
            overflowY: 'auto' as const,
            overflowX: 'hidden' as const,
            alignItems: sidebarCollapsed ? 'center' : undefined,
            transition: isToggling ? 'all 0.3s ease-out' : undefined,
          }
        : {
            bottom: '0',
            left: '0',
            right: '0',
            display: 'flex',
            flexDir: 'row' as const,
            borderTop: '1px solid',
            borderColor: '#e1e5eb',
            minH: '20',
            alignItems: 'center',
            justifyContent: 'space-around',
            // Grow below the tap area to cover the iPhone home indicator
            pb: 'max(10px, env(safe-area-inset-bottom, 10px))',
            pl: 'env(safe-area-inset-left, 0px)',
            pr: 'env(safe-area-inset-right, 0px)',
            boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
          })}
    >
      {isDesktop && (
        <Flex
          flexShrink={0}
          alignItems="center"
          justifyContent={sidebarCollapsed ? 'flex-start' : 'space-between'}
          flexDir={sidebarCollapsed ? 'column' : 'row'}
          gap="2"
          mb={sidebarCollapsed ? '8' : '10'}
          minH="10"
          w={sidebarCollapsed ? 'full' : undefined}
        >
          <chakra.a
            href="/organizations"
            display="flex"
            alignItems="center"
            gap={sidebarCollapsed ? '0' : '3'}
            textDecoration="none"
            color="inherit"
            minW="0"
            justifyContent={sidebarCollapsed ? 'center' : undefined}
            w={sidebarCollapsed ? 'full' : undefined}
            aria-label="Volyx home"
          >
            <chakra.img
              src="/assets/images/volyx-mark.png"
              alt=""
              w={sidebarCollapsed ? '9' : '10'}
              h={sidebarCollapsed ? '9' : '10'}
              objectFit="contain"
              flexShrink={0}
              display="block"
              mx={sidebarCollapsed ? 'auto' : undefined}
            />
            <Box
              as="span"
              fontSize="lg"
              fontWeight="bold"
              color="volyx.primary"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
              maxW={sidebarCollapsed ? '0' : '120px'}
              minW={sidebarCollapsed ? '0' : undefined}
              w={sidebarCollapsed ? '0' : undefined}
              opacity={sidebarCollapsed ? 0 : 1}
              m={sidebarCollapsed ? '0' : undefined}
              p={sidebarCollapsed ? '0' : undefined}
            >
              Volyx
            </Box>
          </chakra.a>
          <chakra.button
            display="flex"
            alignItems="center"
            justifyContent="center"
            w="8"
            h="8"
            p="0"
            border="none"
            bg="#f1f5f9"
            borderRadius="lg"
            cursor="pointer"
            color="#5a6c84"
            flexShrink={0}
            transition="all 0.2s"
            _hover={{ bg: '#e2e8f0', color: 'volyx.primary' }}
            _active={{ transform: 'scale(0.95)' }}
            onClick={toggleSidebar}
            aria-label={sidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {sidebarExpanded ? (
              <ChevronLeftIcon width={20} height={20} />
            ) : (
              <ChevronRightIcon width={20} height={20} />
            )}
          </chakra.button>
        </Flex>
      )}

      <Flex
        flexDir={isDesktop ? 'column' : 'row'}
        flex={isDesktop ? '1' : undefined}
        minH={isDesktop ? '0' : undefined}
        gap={isDesktop ? '3' : undefined}
        alignItems={!isDesktop ? 'center' : undefined}
        justifyContent={!isDesktop ? 'space-around' : undefined}
        w="full"
        h={!isDesktop ? 'full' : undefined}
      >
        {tabs.map((tab) => {
          const active = isActive(tab.path);
          return (
            <chakra.button
              type="button"
              key={tab.key}
              display="flex"
              flexDir="row"
              cursor="pointer"
              border="none"
              alignSelf="stretch"
              position="relative"
              textAlign="left"
              minH={isDesktop ? '60px' : undefined}
              touchAction="manipulation"
              {...(isDesktop
                ? {
                    alignItems: 'center',
                    justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                    w: 'full',
                    px: sidebarCollapsed ? '3' : '4',
                    py: sidebarCollapsed ? '3' : '4',
                    mb: '0',
                    borderRadius: 'xl',
                    gap: sidebarCollapsed ? '0' : '3',
                    bg: active ? '#f1f5f9' : 'transparent',
                    _hover: { bg: active ? '#f1f5f9' : '#f8fafc' },
                  }
                : {
                    justifyContent: 'center',
                    alignItems: 'center',
                    flex: '1',
                    p: '2',
                    bg: 'transparent',
                    _hover: { opacity: 0.7 },
                  })}
              onClick={() => handleTabClick(tab.path)}
              aria-label={tab.title}
            >
              {/* Wrapper so the badge anchors to the icon, not to the whole row --
                  on desktop the row also holds the label and stretches. */}
              <Box position="relative" display="flex" flexShrink={0}>
                <TabIcon
                  focused={active}
                  OutlineIcon={tab.OutlineIcon}
                  SolidIcon={tab.SolidIcon}
                  tabKey={tab.key}
                />
                {tab.key === 'communication' && chatUnread > 0 && (
                  <Box
                    position="absolute"
                    top="-4px"
                    right="-8px"
                    minW="18px"
                    h="18px"
                    px="1"
                    borderRadius="full"
                    bg="volyx.action"
                    color="white"
                    fontSize="10px"
                    fontWeight="bold"
                    lineHeight="18px"
                    textAlign="center"
                    pointerEvents="none"
                    fontVariantNumeric="tabular-nums"
                    aria-label={`${chatUnread} unread messages`}
                  >
                    {formatUnreadCount(chatUnread)}
                  </Box>
                )}
              </Box>
              {isDesktop && (
                <Box
                  as="span"
                  flex="1"
                  fontSize="sm"
                  fontWeight={active ? 'semibold' : 'medium'}
                  color={active ? 'volyx.primary' : '#5a6c84'}
                  whiteSpace="nowrap"
                  overflow="hidden"
                  display="flex"
                  alignItems="center"
                  pointerEvents="none"
                  maxW={sidebarCollapsed ? '0' : '180px'}
                  minW={sidebarCollapsed ? '0' : undefined}
                  w={sidebarCollapsed ? '0' : undefined}
                  opacity={sidebarCollapsed ? 0 : 1}
                  m={sidebarCollapsed ? '0' : undefined}
                  p={sidebarCollapsed ? '0' : undefined}
                  border={sidebarCollapsed ? '0' : undefined}
                >
                  {tab.title}
                </Box>
              )}
            </chakra.button>
          );
        })}
      </Flex>

      {isDesktop && <Box flex="1" minH="6" />}

      {isDesktop && (
        <chakra.button
          type="button"
          display="flex"
          flexDir="row"
          alignItems="center"
          justifyContent={sidebarCollapsed ? 'center' : 'flex-start'}
          w="full"
          px={sidebarCollapsed ? '3' : '4'}
          py={sidebarCollapsed ? '3' : '4'}
          mt="6"
          minH="60px"
          border="none"
          borderRadius="xl"
          gap={sidebarCollapsed ? '0' : '3'}
          bg="transparent"
          cursor="pointer"
          color="#64748b"
          _hover={{ bg: '#fef2f2', color: '#b91c1c' }}
          onClick={handleLogout}
          aria-label="Log out"
        >
          <LogoutOutline
            width={22}
            height={22}
            style={{ display: 'block', flexShrink: 0, color: 'inherit' }}
          />
          <Box
            as="span"
            flex="1"
            fontSize="sm"
            fontWeight="medium"
            whiteSpace="nowrap"
            overflow="hidden"
            pointerEvents="none"
            maxW={sidebarCollapsed ? '0' : '180px'}
            minW={sidebarCollapsed ? '0' : undefined}
            w={sidebarCollapsed ? '0' : undefined}
            opacity={sidebarCollapsed ? 0 : 1}
            m={sidebarCollapsed ? '0' : undefined}
            p={sidebarCollapsed ? '0' : undefined}
          >
            Log out
          </Box>
        </chakra.button>
      )}
    </Box>
  );
}
